import { HeaderData } from './header.class';
import { HeaderSettingVisibility, TemplateSettingFlags, isBlank } from './persistence.utils';

/**
 * The meeting link of a poll header (calendar quick wins C, decisions K1 = A and K1b = C): one https url, shown
 * right below the location on the vote page, carried by the calendar entries (ICS URL / LOCATION, Google and
 * Outlook location) and the vote confirmation mail. Pure: nothing from Angular, Firebase or the browser.
 *
 * - only https - the editor shows an error for http:// instead of upgrading it, javascript: and the like are
 *   invalid; the rule on whoo/header/{o}/{p}/meetingLink checks the https:// prefix and the 500 characters as well
 * - known providers (allowlist below) get their name as link text; the mail links only to them, every other host
 *   is a plain text there - the vote page and the calendar always carry the full url
 * - not every template offers the link (C2, Kai 2026-11-02: not invitation, potluck, duties): there a stored link is
 *   never shown - meetingLinkVisibility(flags).available
 */

/** the rule on whoo/header/{o}/{p}/meetingLink allows the same length (database.rules.json) - change both together */
export const MEETING_LINK_MAX_LENGTH = 500;

export type MeetingLinkError = 'notHttps' | 'invalid' | 'tooLong';

export interface NormalizedMeetingLink {
    /** the url to store; undefined = the field is empty (cleared) or the input is invalid */
    value?: string;
    error?: MeetingLinkError;
}

export type MeetingProvider = 'zoom' | 'googleMeet' | 'teams' | 'webex' | 'whereby' | 'jitsi';

/** brand names, the same in every language */
export const MEETING_PROVIDER_NAMES: Record<MeetingProvider, string> = {
    zoom: 'Zoom',
    googleMeet: 'Google Meet',
    teams: 'Microsoft Teams',
    webex: 'Webex',
    whereby: 'Whereby',
    jitsi: 'Jitsi Meet',
};

/** host exactly or a subdomain of it ("us02web.zoom.us", "company.webex.com") - compared on the end of the host name, never with includes */
const PROVIDER_DOMAINS: [string, MeetingProvider][] = [
    ['zoom.us', 'zoom'],
    ['zoom.com', 'zoom'],
    ['meet.google.com', 'googleMeet'],
    ['teams.microsoft.com', 'teams'],
    ['teams.live.com', 'teams'],
    ['webex.com', 'webex'],
    ['whereby.com', 'whereby'],
    ['meet.jit.si', 'jitsi'],
];

export interface MeetingLinkDescription {
    url: string;
    /** the host as the URL parser gives it: lowercase, an IDN in its punycode form (xn--…) */
    host: string;
    provider?: MeetingProvider;
    isKnownProvider: boolean;
}

/**
 * The input of the editor as the url to store.
 * - trimmed; empty or only blanks → value undefined (the field is cleared)
 * - "zoom.us/j/123" or "//zoom.us/j/123" → "https://zoom.us/j/123"
 * - http:// → error notHttps (not upgraded silently - the owner should see it); any other scheme → invalid
 * - parsed with URL: a host with a dot, no user or password in front of the host ("https://meet.google.com@evil.io"
 *   would show one host and open another)
 * - stored as the parser writes it back (lowercase host, punycode, encoded path), at most MEETING_LINK_MAX_LENGTH
 */
export function normalizeMeetingLink(input: string | null | undefined): NormalizedMeetingLink {
    const text = typeof input === 'string' ? input.trim() : '';
    if (text === '') return { value: undefined };
    let candidate: string;
    if (/^https:\/\//i.test(text)) candidate = text;
    else if (/^http:\/\//i.test(text)) return { error: 'notHttps' };
    else if (text.startsWith('//')) candidate = 'https:' + text;
    // a scheme ("javascript:", "mailto:", "ftp://") - but not a host with a port ("zoom.us:443/j/1")
    else if (/^[a-z][a-z0-9+.-]*:(?!\d)/i.test(text)) return { error: 'invalid' };
    else candidate = 'https://' + text;

    if (/\s/.test(candidate)) return { error: 'invalid' };
    let url: URL;
    try {
        url = new URL(candidate);
    } catch (error) {
        return { error: 'invalid' };
    }
    if (url.protocol !== 'https:' || url.username !== '' || url.password !== '') return { error: 'invalid' };
    if (!isPlausibleHost(url.hostname)) return { error: 'invalid' };
    if (url.href.length > MEETING_LINK_MAX_LENGTH) return { error: 'tooLong' };
    return { value: url.href };
}

/** a host name with at least one dot, no empty label and a top level domain of letters (or punycode) */
function isPlausibleHost(hostname: string): boolean {
    const labels = hostname.split('.');
    if (labels.length < 2 || labels.some(label => label === '')) return false;
    return /^([a-z]{2,63}|xn--[a-z0-9-]+)$/.test(labels[labels.length - 1]);
}

/** host and provider of a stored meeting link; null when it does not parse as an https url */
export function describeMeetingLink(url: string): MeetingLinkDescription | null {
    let parsed: URL;
    try {
        parsed = new URL(url);
    } catch (error) {
        return null;
    }
    if (parsed.protocol !== 'https:') return null;
    const host = parsed.hostname;
    const match = PROVIDER_DOMAINS.find(([domain]) => host === domain || host.endsWith('.' + domain));
    return match
        ? { url, host, provider: match[1], isKnownProvider: true }
        : { url, host, isKnownProvider: false };
}

/** the link text: the provider's name, otherwise the host */
export function meetingLinkText(description: MeetingLinkDescription): string {
    return description.provider ? MEETING_PROVIDER_NAMES[description.provider] : description.host;
}

/** what the poll template says about the meeting link: offered at all, and behind a toggle */
export interface MeetingLinkVisibility extends HeaderSettingVisibility {
    available: boolean;
}

/** the meeting link part of the template flags - the input of headerMeetingLink */
export function meetingLinkVisibility(flags: TemplateSettingFlags): MeetingLinkVisibility {
    return { available: flags.meetingLinkAvailable, hasAdvancedToggle: flags.meetingLinkAdvanced };
}

/**
 * The meeting link as shown - the rule of the location (headerLocation) plus availability: the template offers the
 * link, there is a text, and either the link has no toggle (field in the first step or the main settings) or
 * showMeetingLinkAdvanced is on. Gives the normalized url; undefined when not offered, hidden, empty or not a valid
 * https url (old or foreign values). A link stored in a template without it (an invitation of before C2, a direct
 * write) stays in the header but shows nowhere - vote page, calendar and mail alike.
 */
export function headerMeetingLink(header: HeaderData | null | undefined, visibility: MeetingLinkVisibility): string | undefined {
    if (!visibility.available || !header || isBlank(header.meetingLink)) return undefined;
    if (visibility.hasAdvancedToggle && !header.showMeetingLinkAdvanced) return undefined;
    return normalizeMeetingLink(header.meetingLink).value;
}
